/**
 * Pure presentation of "The Climb" (specs/rank-ladder §7). The connected
 * RankLadderScreen feeds it live data; stories/tests feed it props. Renders a tall
 * scrollable mountain scene with all 10 ranks as camps on the trail (Icon summit →
 * Rookie basecamp), highlights the current camp, and auto-centers on it once the
 * viewport is measured (positions are known from the fixed slot table — no retry loop).
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Animated,
  Easing,
  Pressable,
  ScrollView,
  Text,
  View,
  type LayoutChangeEvent,
} from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, {
  Path,
  Defs,
  LinearGradient as SvgGradient,
  Stop,
} from 'react-native-svg';
import type { RankSceneNode } from './rankLadder.helper';
import { getRankLadderStyles } from './RankLadderView.styles';
import {
  BASECAMP_LABEL_TOP,
  CAMP_X_LEFT,
  FLOAT_DISTANCE,
  FLOAT_DURATION,
  getSceneHeadroom,
  MOUNTAIN_PATH,
  RANK_LADDER_COLORS,
  RANK_LADDER_TEST_IDS,
  RIDGE_PATH,
  SCENE_HEIGHT,
  SCENE_WIDTH,
  SCROLL_CENTER_RATIO,
  SKY_LOCATIONS,
  SKY_STOPS,
  SNOW_PATH,
  TRAIL_PATH,
} from './rankLadder.constants';

const CAMP_BOX_W = 170;
/** Half the BASECAMP label's own width — it centres on the Rookie column. */
const BASECAMP_LABEL_HALF_W = 48;

export interface RankLadderViewProps {
  nodes: RankSceneNode[];
  onBack: () => void;
  safeAreaTop: number;
  testID?: string;
}

export const RankLadderView: React.FC<RankLadderViewProps> = ({
  nodes,
  onBack,
  safeAreaTop,
  testID,
}) => {
  const styles = useMemo(() => getRankLadderStyles(safeAreaTop), [safeAreaTop]);
  const scrollRef = useRef<ScrollView>(null);
  const float = useRef(new Animated.Value(0)).current;
  // Everything in the scene (scenery layers included) is pushed below the floating header.
  const headroom = getSceneHeadroom(safeAreaTop);

  const currentTop = useMemo(
    () => nodes.find((n) => n.isCurrent)?.top ?? null,
    [nodes],
  );

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(float, {
          toValue: 1,
          duration: FLOAT_DURATION / 2,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(float, {
          toValue: 0,
          duration: FLOAT_DURATION / 2,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [float]);

  const floatY = float.interpolate({ inputRange: [0, 1], outputRange: [0, -FLOAT_DISTANCE] });

  // Center the current camp once the viewport height is known (slot tops are static).
  const centerOnCurrent = useCallback(
    (viewportHeight: number) => {
      if (currentTop == null) return;
      const target = currentTop + headroom - viewportHeight * SCROLL_CENTER_RATIO;
      scrollRef.current?.scrollTo({ y: Math.max(0, target), animated: false });
    },
    [currentTop, headroom],
  );

  const handleScrollLayout = useCallback(
    (e: LayoutChangeEvent) => centerOnCurrent(e.nativeEvent.layout.height),
    [centerOnCurrent],
  );

  return (
    <View style={styles.screen} testID={testID ?? RANK_LADDER_TEST_IDS.SCREEN}>
      <ScrollView
        ref={scrollRef}
        style={styles.screen}
        contentContainerStyle={styles.scene}
        onLayout={handleScrollLayout}
        showsVerticalScrollIndicator={false}
        testID={RANK_LADDER_TEST_IDS.SCROLL}
      >
        <View style={styles.sceneColumn}>
          {/* sky */}
          <LinearGradient
            colors={SKY_STOPS}
            locations={SKY_LOCATIONS}
            style={styles.skyFill}
            pointerEvents="none"
          />

          {/* mountain + snow + foreground ridge */}
          <Svg
            width="100%"
            height={SCENE_HEIGHT}
            viewBox={`0 0 ${SCENE_WIDTH} ${SCENE_HEIGHT}`}
            preserveAspectRatio="none"
            style={styles.sceneFill}
            pointerEvents="none"
          >
            <Defs>
              <SvgGradient id="rlMtn" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={RANK_LADDER_COLORS.MOUNTAIN_TOP} />
                <Stop offset="1" stopColor={RANK_LADDER_COLORS.MOUNTAIN_BOTTOM} />
              </SvgGradient>
              <SvgGradient id="rlSnow" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={RANK_LADDER_COLORS.SNOW_TOP} />
                <Stop offset="1" stopColor={RANK_LADDER_COLORS.SNOW_BOTTOM} />
              </SvgGradient>
            </Defs>
            <Path d={MOUNTAIN_PATH} fill="url(#rlMtn)" />
            <Path d={SNOW_PATH} fill="url(#rlSnow)" fillOpacity={0.9} />
            <Path d={RIDGE_PATH} fill={RANK_LADDER_COLORS.RIDGE} fillOpacity={0.85} />
          </Svg>

          {/* winding dashed trail */}
          <Svg
            width="100%"
            height={SCENE_HEIGHT}
            viewBox={`0 0 ${SCENE_WIDTH} ${SCENE_HEIGHT}`}
            preserveAspectRatio="none"
            style={styles.sceneFill}
            pointerEvents="none"
          >
            <Path
              d={TRAIL_PATH}
              fill="none"
              stroke={RANK_LADDER_COLORS.TRAIL}
              strokeWidth={4}
              strokeLinecap="round"
              strokeDasharray="2 14"
              strokeOpacity={0.55}
            />
          </Svg>

          {nodes.map((node) => (
            <Camp
              key={node.index}
              node={node}
              styles={styles}
              floatY={floatY}
              headroom={headroom}
            />
          ))}

          <Text
            style={[
              styles.basecampLabel,
              {
                top: BASECAMP_LABEL_TOP + headroom,
                left: CAMP_X_LEFT - BASECAMP_LABEL_HALF_W,
              },
            ]}
            testID={RANK_LADDER_TEST_IDS.BASECAMP}
          >
            BASECAMP
          </Text>
        </View>
      </ScrollView>

      {/* sticky header */}
      <View style={styles.header} pointerEvents="box-none">
        {/* Fixed-height bar: the title centres in it and the arrow is pinned to the left
            edge of the same bar, so the two share a centre line. */}
        <View style={styles.headerBar} pointerEvents="box-none">
          <Pressable
            onPress={onBack}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={({ pressed }) => [styles.backButton, pressed && styles.backPressed]}
            accessibilityRole="button"
            accessibilityLabel="Go back"
            testID={RANK_LADDER_TEST_IDS.BACK}
          >
            <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
              <Path d="M19 12H5" stroke={RANK_LADDER_COLORS.BACK_ICON} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M12 19l-7-7 7-7" stroke={RANK_LADDER_COLORS.BACK_ICON} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
          </Pressable>
          <Text style={styles.headerText}>THE CLIMB</Text>
        </View>
      </View>
    </View>
  );
};

interface CampProps {
  node: RankSceneNode;
  styles: ReturnType<typeof getRankLadderStyles>;
  floatY: Animated.AnimatedInterpolation<number>;
  headroom: number;
}

const Camp: React.FC<CampProps> = ({ node, styles, floatY, headroom }) => {
  const floats = node.isCurrent || (node.isSummit && !node.isNext);
  const badge = (
    <Image
      source={node.badge}
      style={{ width: node.size, height: node.size, opacity: node.opacity }}
      contentFit="contain"
      alt=""
    />
  );

  return (
    <View
      style={[
        // The slot is the badge's *centre*, so the box is pulled back by half the badge on
        // both axes. Centring on y (not top-anchoring) is what keeps the even 146px camp
        // spacing true for differently-sized badges, and grows the current camp about its
        // own trail switchback instead of pushing it down off the turn.
        styles.camp,
        {
          top: node.top - node.size / 2 + headroom,
          left: node.x - CAMP_BOX_W / 2,
          width: CAMP_BOX_W,
        },
      ]}
      testID={
        node.isCurrent
          ? RANK_LADDER_TEST_IDS.CURRENT_ROW
          : `${RANK_LADDER_TEST_IDS.ROW}-${node.index}`
      }
    >
      {node.isCurrent && <View style={styles.youGlow} pointerEvents="none" />}
      {node.isSummit && !node.isCurrent && <View style={styles.summitGlow} pointerEvents="none" />}
      {node.isNext && <View style={styles.nextRing} pointerEvents="none" />}

      {node.isCurrent && (
        <View style={styles.youPin} testID={RANK_LADDER_TEST_IDS.YOU_MARKER}>
          <Text style={styles.youPinText}>📍 YOU&apos;RE HERE</Text>
        </View>
      )}

      {floats ? (
        <Animated.View style={{ transform: [{ translateY: floatY }] }}>{badge}</Animated.View>
      ) : (
        badge
      )}

      {node.isCurrent ? (
        <>
          <Text style={styles.youRank}>
            {node.name} · LV {node.level}
          </Text>
          {node.xpProgress != null && (
            <View
              style={styles.xpTrack}
              testID={RANK_LADDER_TEST_IDS.XP_BAR}
              accessibilityValue={{ now: Math.round(node.xpProgress * 100), min: 0, max: 100 }}
            >
              <LinearGradient
                colors={[RANK_LADDER_COLORS.XP_FILL_FROM, RANK_LADDER_COLORS.XP_FILL_TO]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={[styles.xpFill, { width: `${Math.round(node.xpProgress * 100)}%` }]}
              />
            </View>
          )}
          {node.xpLabel != null && <Text style={styles.xpLabel}>{node.xpLabel}</Text>}
        </>
      ) : node.isNext ? (
        <>
          <Text style={styles.nextName} testID={RANK_LADDER_TEST_IDS.NEXT_MARKER}>
            {node.name}
          </Text>
          {node.unlockCoins != null && (
            <View style={styles.unlockPill} testID={RANK_LADDER_TEST_IDS.UNLOCK_PILL}>
              <Svg width={9} height={9} viewBox="0 0 24 24">
                <Path
                  d="M5 11 h14 v10 h-14 Z M8 11 V8 a4 4 0 0 1 8 0"
                  fill="none"
                  stroke={RANK_LADDER_COLORS.UNLOCK_TEXT}
                  strokeWidth={2.6}
                  strokeLinecap="round"
                />
              </Svg>
              <Text style={styles.unlockText}>UNLOCK · {node.unlockCoins.toLocaleString()} COINS</Text>
            </View>
          )}
        </>
      ) : node.isSummit ? (
        <Text style={styles.summitLabel} testID={RANK_LADDER_TEST_IDS.SUMMIT}>
          {node.name} · SUMMIT
        </Text>
      ) : (
        <Text style={[styles.badgeLabel, node.role === 'below' ? styles.belowLabel : styles.aboveLabel]}>
          {node.name}
          {node.role === 'below' ? ' ✓' : ''}
        </Text>
      )}
    </View>
  );
};
